import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { apiErrorMessage, serviceCatalogService, taxTypesService } from '../../services/api';
import { EmptyState, PageHeader, TableSkeleton } from '../../components/ui';
import { useShop } from '../../contexts/ShopContext';
import { ServiceCatalogItem, TaxType } from '../../types';
import { APP_LOCALE } from '../../utils/locale';

/**
 * The shop's repeatable services.
 *
 * These are templates. Pricing an invoice line copies the values across, so
 * changing a price here never reprices an invoice that has already been
 * issued — the same rule that governs a charged tax rate.
 */

interface CatalogForm {
  name: string;
  description: string;
  defaultPrice: string;
  defaultQuantity: string;
  taxTypeId: string;
  isActive: boolean;
}

const EMPTY_FORM: CatalogForm = {
  name: '',
  description: '',
  defaultPrice: '0.00',
  defaultQuantity: '1',
  taxTypeId: '',
  isActive: true,
};

const money = (value: number | string) =>
  Number(value).toLocaleString(APP_LOCALE, { style: 'currency', currency: 'USD' });

const ServiceCatalogPage: React.FC = () => {
  const { selectedShop } = useShop();

  const [items, setItems] = useState<ServiceCatalogItem[]>([]);
  const [taxTypes, setTaxTypes] = useState<TaxType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [editing, setEditing] = useState<ServiceCatalogItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CatalogForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ServiceCatalogItem | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [catalog, types] = await Promise.all([
        serviceCatalogService.list(),
        // Tax types are optional here; a service with none uses the shop default.
        taxTypesService.getTaxTypes().catch(() => [] as TaxType[]),
      ]);
      setItems(catalog);
      setTaxTypes(types);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load the service catalog.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (item: ServiceCatalogItem) => {
    setEditing(item);
    setForm({
      name: item.name,
      description: item.description ?? '',
      defaultPrice: Number(item.defaultPrice).toFixed(2),
      defaultQuantity: String(Number(item.defaultQuantity)),
      taxTypeId: item.taxTypeId ?? '',
      isActive: item.isActive,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      defaultPrice: Number(form.defaultPrice) || 0,
      defaultQuantity: Number(form.defaultQuantity) || 1,
      // "" means "use the shop's default type", which the API expresses as null.
      taxTypeId: form.taxTypeId || undefined,
      isActive: form.isActive,
    };

    try {
      if (editing) {
        await serviceCatalogService.update(editing.id, payload);
        setNotice(`${payload.name} updated`);
      } else {
        await serviceCatalogService.create(payload);
        setNotice(`${payload.name} added`);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the service.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;

    try {
      await serviceCatalogService.remove(pendingDelete.id);
      setNotice(`${pendingDelete.name} removed`);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not remove the service.'));
    } finally {
      setPendingDelete(null);
    }
  };

  const field =
    (key: keyof CatalogForm) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <PageHeader
        eyebrow={selectedShop?.name}
        title="Service catalog"
        subtitle="Jobs you quote often, with the price and tax treatment they usually carry. Adding one to an invoice copies these values across — later edits here never change an invoice already issued."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New service
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <TableSkeleton rows={4} columns={5} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<MenuBookIcon />}
            title="No services yet"
            description="Add the jobs you quote most often so pricing an invoice is one click."
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                New service
              </Button>
            }
          />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Tax</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontWeight={600}>{item.name}</Typography>
                        {!item.isActive && (
                          <Chip size="small" label="Retired" variant="outlined" />
                        )}
                      </Stack>
                      {item.description && (
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {money(item.defaultPrice)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {Number(item.defaultQuantity)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.taxType?.name ?? 'Shop default'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(item)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setPendingDelete(item)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.name}` : 'New service'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              required
              fullWidth
              value={form.name}
              onChange={field('name')}
              helperText="Appears as the invoice line description."
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={form.description}
              onChange={field('description')}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Default price"
                type="number"
                fullWidth
                value={form.defaultPrice}
                onChange={field('defaultPrice')}
                inputProps={{ min: 0, step: '0.01' }}
              />
              <TextField
                label="Default quantity"
                type="number"
                fullWidth
                value={form.defaultQuantity}
                onChange={field('defaultQuantity')}
                inputProps={{ min: 0, step: '0.25' }}
                helperText="Hours for labor, 1 for a flat rate."
              />
            </Stack>
            <TextField
              select
              label="Tax type"
              fullWidth
              value={form.taxTypeId}
              onChange={field('taxTypeId')}
              helperText="Leave as the shop default unless this service is taxed differently."
            >
              <MenuItem value="">Shop default</MenuItem>
              {taxTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, isActive: event.target.checked }))
                  }
                />
              }
              label="Offered on new invoices"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
          >
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add service'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Remove {pendingDelete?.name}?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Invoices that already used this service keep their line exactly as it is —
            removing it only takes it off the picker. Turn it off instead if you might
            offer it again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPendingDelete(null)}>Keep it</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        message={notice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Box sx={{ height: 24 }} />
    </Container>
  );
};

export default ServiceCatalogPage;
